import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MinusIcon, PlusIcon, UsersIcon, BedDoubleIcon, CrownIcon } from 'lucide-react';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
// Minimal stepper row component
function StepperRow({
  label,
  value,
  onDecrement,
  onIncrement
}: {
  label: string;
  value: number;
  onDecrement: () => void;
  onIncrement: () => void;
}) {
  return <div className="flex items-center justify-between py-3" data-id="element-1">
      <span className="font-[Inter,_sans-serif] text-[14px] text-[#2C2420]/75" data-id="element-2">
        {label}
      </span>
      <div className="flex items-center gap-3" data-id="element-3">
        <button type="button" onClick={onDecrement} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ddd] bg-white text-[#2C2420]/50 hover:border-[#2C2420]/40 hover:text-[#2C2420] hover:shadow-sm transition-all focus:outline-none" aria-label={`Disminuir ${label}`} data-id="element-4">
          <MinusIcon size={13} strokeWidth={1.5} data-id="element-5" />
        </button>
        <span className="font-[Inter,_sans-serif] text-[16px] font-normal text-[#2C2420] w-5 text-center tabular-nums" data-id="element-6">
          {value}
        </span>
        <button type="button" onClick={onIncrement} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ddd] bg-white text-[#2C2420]/50 hover:border-[#2C2420]/40 hover:text-[#2C2420] hover:shadow-sm transition-all focus:outline-none" aria-label={`Aumentar ${label}`} data-id="element-7">
          <PlusIcon size={13} strokeWidth={1.5} data-id="element-8" />
        </button>
      </div>
    </div>;
}
// Room type card data
const roomTypes = [{
  id: 'Matrimonial',
  label: 'Matrimonial',
  capacity: 'Hasta 2 personas',
  microcopy: 'Ideal para parejas',
  icon: BedDoubleIcon
}, {
  id: 'Familiar',
  label: 'Familiar',
  capacity: 'Hasta 4 personas',
  microcopy: 'Perfecta para familias pequeñas',
  icon: UsersIcon
}, {
  id: 'Suite',
  label: 'Suite',
  capacity: 'Hasta 6 personas',
  microcopy: 'Mejor opción para grupos grandes',
  icon: CrownIcon
}];
export function BookingModal({
  isOpen,
  onClose
}: BookingModalProps) {
  const [llegada, setLlegada] = useState('');
  const [salida, setSalida] = useState('');
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');
  // Guest detail state
  const [adultos, setAdultos] = useState(1);
  const [ninos, setNinos] = useState(0);
  const [habitaciones, setHabitaciones] = useState(1);
  // Manual room type selection
  const [tipoHabitacionManual, setTipoHabitacionManual] = useState('');
  // Derived total personas (replaces the old dropdown)
  const totalPersonas = adultos + ninos;
  // Auto-adjust habitaciones based on total guests (1 room per 4 people)
  const ajustarHabitaciones = (newAdultos: number, newNinos: number) => {
    const total = newAdultos + newNinos;
    let hab = Math.ceil(total / 4);
    if (hab < 1) hab = 1;
    setHabitaciones(hab);
  };
  // Stepper handlers with auto-adjust
  const cambiarAdultos = (cambio: number) => {
    setAdultos(v => {
      const nuevo = Math.max(1, v + cambio);
      ajustarHabitaciones(nuevo, ninos);
      return nuevo;
    });
  };
  const cambiarNinos = (cambio: number) => {
    setNinos(v => {
      const nuevo = Math.max(0, v + cambio);
      ajustarHabitaciones(adultos, nuevo);
      return nuevo;
    });
  };
  // Validation
  const validarReserva = (): boolean => {
    const total = adultos + ninos;
    if (adultos < 1) {
      setError('Debe haber al menos 1 adulto');
      return false;
    }
    if (total > habitaciones * 6) {
      setError('Demasiadas personas para las habitaciones seleccionadas');
      return false;
    }
    if (habitaciones > total) {
      setError('No puedes seleccionar más habitaciones que personas');
      return false;
    }
    return true;
  };
  // Get room type: manual selection takes priority over auto recommendation
  const obtenerTipoHabitacion = (): string => {
    if (tipoHabitacionManual && tipoHabitacionManual !== '') {
      return tipoHabitacionManual;
    }
    const total = adultos + ninos;
    if (total <= 2) return 'Matrimonial';
    if (total <= 4) return 'Familiar';
    return 'Suite';
  };
  // Auto-recommended room type
  const tipoRecomendado = (() => {
    const total = adultos + ninos;
    if (total <= 2) return 'Matrimonial';
    if (total <= 4) return 'Familiar';
    return 'Suite';
  })();
  // Dynamic decision helper text
  const decisionHelper = (() => {
    const total = adultos + ninos;
    if (total <= 1) return 'Para 1 persona recomendamos: Matrimonial';
    if (total === 2) return 'Para 2 personas recomendamos: Matrimonial';
    if (total === 3) return 'Para 3 personas recomendamos: Familiar';
    if (total === 4) return 'Para 4 personas recomendamos: Familiar';
    if (total === 5) return 'Para 5 personas recomendamos: Suite';
    if (total === 6) return 'Para 6 personas recomendamos: Suite o 2 habitaciones familiares';
    return `Para ${total} personas recomendamos: ${Math.ceil(total / 4)} habitaciones familiares o ${Math.ceil(total / 6)} suites`;
  })();
  const handleConfirmar = () => {
    if (!llegada || !salida || !nombre) {
      setError('Por favor completa todos los campos');
      return;
    }
    if (!validarReserva()) return;
    setError('');
    function formatearFecha(fecha: string) {
      const f = new Date(fecha);
      return f.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    }
    const llegadaFormateada = formatearFecha(llegada);
    const salidaFormateada = formatearFecha(salida);
    const total = adultos + ninos;
    const personas = String(total);
    const tipoHabitacion = obtenerTipoHabitacion();
    // Facebook Pixel — CompleteRegistration event
    if (typeof (window as any).fbq === 'function') {
      ;
      (window as any).fbq('track', 'CompleteRegistration');
    }
    // Abrir WhatsApp inmediatamente (evita bloqueo de popup)
    const mensaje = `Hola, quiero confirmar mi reserva en Hacienda El Rosario:\nNombre: ${nombre}\nHuéspedes: ${personas}\nAdultos: ${adultos}\nNiños: ${ninos}\nTotal personas: ${total}\nHabitaciones: ${habitaciones}\nTipo de habitación: ${tipoHabitacion}\nLlegada: ${llegadaFormateada}\nSalida: ${salidaFormateada}`;
    const whatsappUrl = 'https://wa.me/584246782227?text=' + encodeURIComponent(mensaje);
    window.open(whatsappUrl, '_blank');
    // Enviar a Google Sheets en background
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get('utm_source') || '';
    const utm_campaign = params.get('utm_campaign') || '';
    const utm_content = params.get('utm_content') || '';
    const sheetURL = 'https://script.google.com/macros/s/AKfycbxDzb067uONmydeX0w4xRVqBMajI5wXS58RgqU_vA_KHnTPRauo4Kp78ztxygBDMYKo/exec';
    const query = `?nombre=${encodeURIComponent(nombre)}&personas=${personas}&adultos=${adultos}&ninos=${ninos}&total=${total}&habitaciones=${habitaciones}&tipoHabitacion=${encodeURIComponent(tipoHabitacion)}&llegada=${encodeURIComponent(llegadaFormateada)}&salida=${encodeURIComponent(salidaFormateada)}&utm_source=${utm_source}&utm_campaign=${utm_campaign}&utm_content=${utm_content}`;
    fetch(sheetURL + query).catch(() => {});
  };
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  // Room type hint
  const roomHint = habitaciones === 1 ? 'Habitación Familiar (hasta 4 personas)' : `${habitaciones} Habitaciones Familiares (hasta ${habitaciones * 4} personas)`;
  // Currently active room type (what will be sent)
  const activeRoomType = obtenerTipoHabitacion();
  return <AnimatePresence data-id="element-9">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4" onClick={handleBackdropClick} data-id="element-10">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative w-full max-w-[420px] bg-white rounded-2xl px-7 py-8 md:px-9 md:py-10 text-center max-h-[90vh] overflow-y-auto shadow-2xl" data-id="element-11">
            {/* Close */}
            <button onClick={onClose} className="absolute top-4 right-4 text-[#2C2420]/40 hover:text-[#2C2420] transition-colors focus:outline-none" aria-label="Cerrar" data-id="element-12">
              <XIcon size={18} strokeWidth={1.5} data-id="element-13" />
            </button>

            <h2 className="font-serif text-xl md:text-2xl text-[#2C2420] mb-1" data-id="element-14">
              Verifica disponibilidad
            </h2>
            <p className="font-[Inter,_sans-serif] text-[13px] text-[#2C2420]/40 mb-6" data-id="element-15">
              Completa estos datos para continuar
            </p>

            {/* Date fields */}
            <div className="flex flex-col gap-4 mb-5 text-left" data-id="element-16">
              <div className="grid grid-cols-2 gap-4" data-id="element-17">
                <div data-id="element-18">
                  <label className="block font-[Inter,_sans-serif] text-[10px] uppercase tracking-[0.15em] text-[#2C2420]/45 mb-1.5" data-id="element-19">
                    Llegada
                  </label>
                  <input id="llegada" type="date" value={llegada} onChange={e => setLlegada(e.target.value)} onClick={e => (e.currentTarget as HTMLInputElement).showPicker()} className="w-full bg-transparent border-b border-[#2C2420]/15 pb-2 pt-1 text-[14px] font-light text-[#2C2420] focus:outline-none focus:border-[#2C2420]/40 transition-colors font-[Inter,_sans-serif]" data-id="element-20" />
                </div>
                <div data-id="element-21">
                  <label className="block font-[Inter,_sans-serif] text-[10px] uppercase tracking-[0.15em] text-[#2C2420]/45 mb-1.5" data-id="element-22">
                    Salida
                  </label>
                  <input id="salida" type="date" value={salida} onChange={e => setSalida(e.target.value)} onClick={e => (e.currentTarget as HTMLInputElement).showPicker()} className="w-full bg-transparent border-b border-[#2C2420]/15 pb-2 pt-1 text-[14px] font-light text-[#2C2420] focus:outline-none focus:border-[#2C2420]/40 transition-colors font-[Inter,_sans-serif]" data-id="element-23" />
                </div>
              </div>
            </div>

            {/* Guest detail card */}
            <div className="rounded-xl border border-[#eee] bg-[#FAFAF8] p-4 mb-4 text-left" data-id="element-24">
              <p className="font-[Inter,_sans-serif] text-[10px] uppercase tracking-[0.15em] text-[#2C2420]/45 mb-2" data-id="element-25">
                Huéspedes
              </p>
              <StepperRow label="Adultos" value={adultos} onDecrement={() => cambiarAdultos(-1)} onIncrement={() => cambiarAdultos(1)} data-id="element-26" />
              <div className="h-[1px] bg-[#2C2420]/5 my-0.5" data-id="element-27" />
              <StepperRow label="Niños" value={ninos} onDecrement={() => cambiarNinos(-1)} onIncrement={() => cambiarNinos(1)} data-id="element-28" />
              <div className="h-[1px] bg-[#2C2420]/5 my-0.5" data-id="element-29" />
              <StepperRow label="Habitaciones" value={habitaciones} onDecrement={() => setHabitaciones(v => Math.max(1, v - 1))} onIncrement={() => setHabitaciones(v => v + 1)} data-id="element-30" />
              {/* Room type hint */}
              
            </div>

            {/* Room type selection cards */}
            <div className="mb-5 text-left" data-id="element-31">
              <p className="font-[Inter,_sans-serif] text-[10px] uppercase tracking-[0.15em] text-[#2C2420]/45 mb-3" data-id="element-32">
                Selecciona tu tipo de habitación
              </p>
              <div className="flex flex-col gap-2" data-id="element-33">
                {roomTypes.map(room => {
              const isRecommended = room.id === tipoRecomendado;
              const isSelected = tipoHabitacionManual === room.id;
              const isActive = isSelected || !tipoHabitacionManual && isRecommended;
              const IconComponent = room.icon;
              return <motion.button key={room.id} type="button" onClick={() => {
                // Toggle: if already selected, clear to use recommendation
                if (tipoHabitacionManual === room.id) {
                  setTipoHabitacionManual('');
                } else {
                  setTipoHabitacionManual(room.id);
                }
              }} whileTap={{
                scale: 0.98
              }} className={`
                        relative flex items-center gap-3.5 w-full rounded-xl border p-3.5
                        transition-all duration-200 focus:outline-none cursor-pointer text-left
                        ${isActive ? 'border-[#2C2420]/40 bg-[#2C2420]/[0.03] shadow-[0_1px_8px_rgba(44,36,32,0.08)]' : isRecommended ? 'border-[#2C2420]/25 bg-[#2C2420]/[0.015] shadow-[0_1px_4px_rgba(44,36,32,0.04)] hover:border-[#2C2420]/30' : 'border-[#ddd] bg-white hover:border-[#2C2420]/20'}
                      `} data-id="element-34">
                      {/* Icon */}
                      <div className={`
                          flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-200
                          ${isActive ? 'bg-[#2C2420] text-white' : 'bg-[#F5F3F0] text-[#2C2420]/40'}
                        `} data-id="element-35">
                        <IconComponent size={18} strokeWidth={1.5} data-id="element-36" />
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0" data-id="element-37">
                        <div className="flex items-center gap-2" data-id="element-38">
                          <span className={`
                              font-[Inter,_sans-serif] text-[14px] transition-colors duration-200
                              ${isActive ? 'text-[#2C2420] font-medium' : 'text-[#2C2420]/70 font-normal'}
                            `} data-id="element-39">
                            {room.label}
                          </span>
                          {isRecommended && <span className="font-[Inter,_sans-serif] text-[9px] uppercase tracking-wider text-[#2C2420]/50 bg-[#2C2420]/[0.06] px-1.5 py-0.5 rounded font-medium" data-id="element-40">
                              Recomendada
                            </span>}
                        </div>
                        <span className="font-[Inter,_sans-serif] text-[12px] text-[#2C2420]/40" data-id="element-41">
                          {room.capacity}
                        </span>
                        <span className="block font-[Inter,_sans-serif] text-[11px] text-[#2C2420]/30 italic mt-0.5" data-id="element-42">
                          {room.microcopy}
                        </span>
                      </div>

                      {/* Selection indicator */}
                      <div className={`
                          flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200
                          ${isActive ? 'border-[#2C2420] bg-[#2C2420]' : 'border-[#ddd] bg-white'}
                        `} data-id="element-43">
                        {isActive && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="h-2 w-2 rounded-full bg-white" data-id="element-44" />}
                      </div>
                    </motion.button>;
            })}
              </div>
              {/* Decision helper text */}
              <p className="text-center font-[Inter,_sans-serif] text-[12px] text-[#666] mt-3 font-light" data-id="element-45">
                {decisionHelper}
              </p>
            </div>

            {/* Name field */}
            <div className="text-left mb-6" data-id="element-46">
              <label className="block font-[Inter,_sans-serif] text-[10px] uppercase tracking-[0.15em] text-[#2C2420]/45 mb-1.5" data-id="element-47">
                Nombre completo
              </label>
              <input id="nombre" type="text" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre" className="w-full bg-transparent border-b border-[#2C2420]/15 pb-2 pt-1 text-[14px] font-light text-[#2C2420] placeholder:text-[#2C2420]/25 focus:outline-none focus:border-[#2C2420]/40 transition-colors font-[Inter,_sans-serif]" data-id="element-48" />
            </div>

            {error && <p className="text-red-500 text-[12px] mb-4 font-[Inter,_sans-serif]" data-id="element-49">
                {error}
              </p>}

            {/* CTA Button */}
            <button onClick={handleConfirmar} className="w-full py-4 rounded-xl bg-[#2C2420] text-white font-serif text-[15px] tracking-wide hover:bg-[#3a322d] transition-colors focus:outline-none cursor-pointer" data-id="element-50">
              Confirmar disponibilidad en WhatsApp →
            </button>

            {/* Trust line */}
            <p className="font-[Inter,_sans-serif] mt-4 text-[#999] text-[12px] font-light leading-relaxed" data-id="element-51">
              Te responderemos en minutos · Estadías desde $120 por noche ·
              desayuno incluido
            </p>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}